import {
  productsIndex_AU,
  productsIndex_NZ,
  productsSearchIndex_AU,
  productsSearchIndex_NZ,
} from 'helpers/constants/algolia';
import { searchClient } from './searchClient';

/**
 * BROWSER PATH. Search client wrapper for the /search results page — the one product listing that
 * queries Algolia from the browser (every other listing is server-preloaded): the first request for a given
 * (products index, query, Configure filters) loads the ENTIRE result set from
 * Algolia (probe + all pages in one batched call), after which every
 * filter / sort / pagination / facet-count request is answered locally from
 * the preloaded data with zero network calls. Requests for non-product
 * indices (content, query suggestions) and the initial pre-Configure request
 * (no filters, no query) pass through to Algolia unchanged.
 *
 * TRANSPORT BOUNDARY — deliberate and load-bearing. Everything in this file talks to Algolia
 * DIRECTLY (`searchClient`). It must never route through the `algoliaSearch/query` backend action or
 * Redis. The two Algolia pulls are now fully bifurcated:
 *
 *   browser-originating (this file)  → Algolia direct            → no backend, no Redis
 *   server-originating  (helpers/algolia/fetch-*.ts) → algoliaSearch/query → Redis (24h)
 *
 * That split is what lets the backend cache shape be optimised for the SSR payload alone. While both
 * paths shared the action, they also shared Redis entries: the cache key is
 * sha256(indexName | params) (see backend AlgoliaApi.generateCacheKey) and both senders built
 * byte-identical params (hitsPerPage 1000, attributesToRetrieve ['*'], facets ['*'],
 * maxValuesPerFacet 999), so on a products index they collided — any slimming done for SSR silently
 * reshaped what this client received too. Re-introducing a backend transport here would re-couple
 * them and break the search page's facet panel, which reads its facet NAMES off the response
 * (`Object.keys(result.facets)` in loadDataset — a field the SSR cache no longer stores).
 */

type AnyRecord = Record<string, any>;

interface Dataset {
  hits: AnyRecord[];
  facetNames: string[];
  renderingContent: AnyRecord;
}

type SortMode = 'default' | 'name_asc' | 'name_desc';

const PAGE_SIZE = 1000; // Algolia max hitsPerPage
const MAX_PRELOAD_PAGES = 30; // hard safety stop (30k records)
const MAX_DATASETS = 8; // in-memory LRU cap

const datasetCache = new Map<string, Promise<Dataset>>();

// Search indices FIRST. The name-sort values drop the '-main' suffix (caroma-au-main → caroma-au_name_asc),
// so with the browse index checked first a name sort on /search resolved to caroma-au and re-fetched a
// different result set instead of re-sorting the search results.
const PRODUCT_INDEX_BASES = Array.from(
  new Set([productsSearchIndex_AU, productsSearchIndex_NZ, productsIndex_AU, productsIndex_NZ].filter(Boolean)),
);

// Sort replicas follow the convention in DesktopSortFacets: base minus '-main' + suffix
const resolveProductIndex = (indexName: string): { base: string; sort: SortMode } | null => {
  for (const base of PRODUCT_INDEX_BASES) {
    if (indexName === base) return { base, sort: 'default' };
    const replicaRoot = base.replace('-main', '');
    if (indexName === `${replicaRoot}_name_asc`) return { base, sort: 'name_asc' };
    if (indexName === `${replicaRoot}_name_desc`) return { base, sort: 'name_desc' };
  }
  return null;
};

// Collects values at a dotted path, descending through arrays (e.g. variants.attributes.colour)
const collectValues = (node: any, parts: string[], index: number, out: any[]) => {
  if (node === null || node === undefined) return;
  if (Array.isArray(node)) {
    for (const item of node) collectValues(item, parts, index, out);
    return;
  }
  if (index === parts.length) {
    out.push(node);
    return;
  }
  if (typeof node === 'object') collectValues(node[parts[index]], parts, index + 1, out);
};

const valuesAt = (record: AnyRecord, attribute: string): any[] => {
  const out: any[] = [];
  collectValues(record, attribute.split('.'), 0, out);
  return out;
};

const matchesFacetEntry = (record: AnyRecord, entry: string): boolean => {
  const separator = entry.indexOf(':');
  if (separator < 0) return true;
  const attribute = entry.slice(0, separator);
  let value = entry.slice(separator + 1);
  let negated = false;
  if (value.startsWith('-')) {
    negated = true;
    value = value.slice(1);
  }
  const matched = valuesAt(record, attribute).some((v) => String(v) === value);
  return negated ? !matched : matched;
};

// facetFilters semantics: AND between top-level entries, OR within nested arrays
const matchesFacetFilters = (record: AnyRecord, facetFilters: any): boolean => {
  if (!facetFilters) return true;
  const groups = Array.isArray(facetFilters) ? facetFilters : [facetFilters];
  return groups.every((group: any) => {
    const entries = Array.isArray(group) ? group : [group];
    if (entries.length === 0) return true;
    return entries.some((entry: any) => matchesFacetEntry(record, String(entry)));
  });
};

const NUMERIC_FILTER_RE = /^\s*(.+?)\s*(<=|>=|!=|=|<|>)\s*(-?\d+(?:\.\d+)?)\s*$/;

const matchesNumericEntry = (record: AnyRecord, entry: string): boolean => {
  const match = entry.match(NUMERIC_FILTER_RE);
  if (!match) return true;
  const [, attribute, operator, rawBound] = match;
  const bound = Number(rawBound);
  const values = valuesAt(record, attribute)
    .map((v) => (typeof v === 'number' ? v : Number(v)))
    .filter((v) => Number.isFinite(v));
  switch (operator) {
    case '<':
      return values.some((v) => v < bound);
    case '<=':
      return values.some((v) => v <= bound);
    case '>':
      return values.some((v) => v > bound);
    case '>=':
      return values.some((v) => v >= bound);
    case '!=':
      return values.every((v) => v !== bound);
    default:
      return values.some((v) => v === bound);
  }
};

// numericFilters semantics: AND between top-level entries, OR within nested arrays
const matchesNumericFilters = (record: AnyRecord, numericFilters: any): boolean => {
  if (!numericFilters) return true;
  const groups = Array.isArray(numericFilters) ? numericFilters : [numericFilters];
  return groups.every((group: any) => {
    const entries = Array.isArray(group) ? group : [group];
    if (entries.length === 0) return true;
    return entries.some((entry: any) => matchesNumericEntry(record, String(entry)));
  });
};

const computeFacets = (hits: AnyRecord[], facetNames: string[], maxValuesPerFacet: number) => {
  const facets: Record<string, Record<string, number>> = {};
  const facetsStats: Record<string, { min: number; max: number; avg: number; sum: number }> = {};

  for (const name of facetNames) {
    const counts = new Map<string, number>();
    let min = Infinity;
    let max = -Infinity;
    let sum = 0;
    let numericCount = 0;
    let allNumeric = true;

    for (const hit of hits) {
      const seen = new Set<string>();
      for (const value of valuesAt(hit, name)) {
        const key = String(value);
        // Skip blank/whitespace-only values: products with a missing or empty facet
        // attribute would otherwise surface as an empty filter option (Algolia's own
        // facet engine omits these).
        if (key.trim() === '') continue;
        if (seen.has(key)) continue;
        seen.add(key);
        counts.set(key, (counts.get(key) || 0) + 1);
        const numeric = typeof value === 'number' ? value : typeof value === 'boolean' ? NaN : Number(value);
        if (Number.isFinite(numeric) && key.trim() !== '') {
          min = Math.min(min, numeric);
          max = Math.max(max, numeric);
          sum += numeric;
          numericCount += 1;
        } else {
          allNumeric = false;
        }
      }
    }

    if (counts.size === 0) continue;
    const sorted = [...counts.entries()]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, maxValuesPerFacet);
    facets[name] = Object.fromEntries(sorted);
    if (allNumeric && numericCount > 0) {
      facetsStats[name] = { min, max, avg: sum / numericCount, sum };
    }
  }

  return { facets, facetsStats };
};

const loadDataset = async (
  base: string,
  query: string,
  filters: string,
  attributesToRetrieve: string[],
  facets: string[],
): Promise<Dataset> => {
  // Fetched DIRECTLY from Algolia (see the transport-boundary note at the top of this file) — never
  // via the backend action / Redis. The dataset is then held in `datasetCache` for the rest of the
  // browser session, so this pull is a one-time cost per (index, query, filters, attributes).
  //
  // `attributesToRetrieve` comes from the caller's InstantSearch `Configure` and MUST already cover the
  // configured facet attributes as well as the tile fields, because every facet count is computed
  // locally from these hits (computeFacets) — a facet whose attribute was not fetched silently counts
  // to nothing and disappears from the panel. Surfaces build the list via
  // helpers/algolia/client-retrieve-attributes.ts, which is what keeps the two in step. It used to be
  // hardcoded to ['*'] for exactly that reason, at a measured cost of 16,651 KB vs 2,491 KB for one
  // 1,000-hit search.
  const baseParams = {
    query,
    filters,
    hitsPerPage: PAGE_SIZE,
    attributesToRetrieve,
    attributesToHighlight: [],
    attributesToSnippet: [],
    analytics: false,
  };

  // First page doubles as the probe: it yields the facet names and the nbPages count, so a listing that
  // fits in one page (≤ PAGE_SIZE) costs a single Algolia call.
  //
  // `facets` is the caller's list, NOT ['*']: with ['*'] Algolia computes and returns counts for every
  // facetable attribute on the index (54 on caroma-au-main, ~28 KB) and none of it is read, because counts
  // are always recomputed locally so they can react to refinements.
  const firstResponse: any = await searchClient.search([
    { indexName: base, params: { ...baseParams, page: 0, facets, maxValuesPerFacet: 999 } },
  ] as any);
  const firstResult = firstResponse.results[0] || {};
  // Only consulted for a `facets: ['*']` request; our surfaces all send explicit lists (see below), so
  // this reflects whichever request happened to load the dataset — deliberately not part of the cache key,
  // since keying on it would fragment one dataset into several identical hit pulls.
  const facetNames = Object.keys(firstResult.facets || {});
  const totalPages = Math.min(Number(firstResult.nbPages) || 0, MAX_PRELOAD_PAGES);

  let hits: AnyRecord[] = Array.isArray(firstResult.hits) ? firstResult.hits : [];
  if (totalPages > 1) {
    const response: any = await searchClient.search(
      Array.from({ length: totalPages - 1 }, (_, i) => ({
        indexName: base,
        params: { ...baseParams, page: i + 1, facets: [] },
      })) as any,
    );
    hits = hits.concat(response.results.flatMap((result: any) => result.hits || []));
  }

  return { hits, facetNames, renderingContent: firstResult.renderingContent || {} };
};

const getDataset = (
  base: string,
  query: string,
  filters: string,
  attributesToRetrieve: string[],
  facets: string[],
): Promise<Dataset> => {
  // The attribute list is part of the key: two surfaces sharing an (index, query, filters) triple
  // but configuring different facets need different datasets, or whichever loaded first would
  // starve the other's facet panel of the attributes it counts.
  const key = `${base} ${query} ${filters} ${attributesToRetrieve.join(',')}`;
  let dataset = datasetCache.get(key);
  if (!dataset) {
    dataset = loadDataset(base, query, filters, attributesToRetrieve, facets);
    datasetCache.set(key, dataset);
    dataset.catch(() => datasetCache.delete(key));
    if (datasetCache.size > MAX_DATASETS) {
      const oldest = datasetCache.keys().next().value;
      if (oldest !== undefined && oldest !== key) datasetCache.delete(oldest);
    }
  } else {
    // refresh LRU position
    datasetCache.delete(key);
    datasetCache.set(key, dataset);
  }
  return dataset;
};

// Empty placeholder for the pre-Configure mount request (a product-index search with no
// filters/query). Returning the unfiltered whole index here pollutes InfiniteHits with a huge
// nbPages, which makes isLastPage false and triggers premature showMore() — leaving short
// single-page categories blank. `__isArtificial` keeps the no-results message suppressed until
// the real filtered query resolves.
const artificialEmptyResult = (request: AnyRecord): AnyRecord => ({
  hits: [],
  nbHits: 0,
  page: Number(request.params?.page) > 0 ? Number(request.params.page) : 0,
  nbPages: 0,
  hitsPerPage: Number(request.params?.hitsPerPage) > 0 ? Number(request.params.hitsPerPage) : 20,
  facets: {},
  exhaustiveNbHits: true,
  exhaustiveFacetsCount: true,
  query: String(request.params?.query ?? ''),
  params: '',
  index: request.indexName,
  processingTimeMS: 0,
  __isArtificial: true,
});

// `name` is a localized object on product records (e.g. { 'en-AU': '...' })
const sortableName = (hit: AnyRecord): string => {
  const name = hit?.name;
  if (typeof name === 'string') return name;
  if (name && typeof name === 'object') {
    const first = Object.values(name).find((value) => typeof value === 'string');
    if (first) return first as string;
  }
  return '';
};

/**
 * Would `attributesToRetrieve` actually fetch the data a facet on `facetName` needs? True on an exact
 * match, when a retrieved parent covers it (`categoryKeys` covers `categoryKeys.en-AU`), or when the
 * retrieve list is ['*'].
 */
const isRetrievedAttribute = (facetName: string, attributesToRetrieve: string[]): boolean =>
  attributesToRetrieve.includes('*') ||
  attributesToRetrieve.some(
    (attribute) =>
      attribute === facetName || facetName.startsWith(`${attribute}.`) || attribute.startsWith(`${facetName}.`),
  );

const normalizeFacetsParam = (facets: any): string[] => {
  if (!facets) return [];
  return Array.isArray(facets) ? facets.map(String) : [String(facets)];
};

const answerFromDataset = async (request: AnyRecord, base: string, sort: SortMode): Promise<AnyRecord> => {
  const params = request.params || {};
  const query = String(params.query ?? '');
  const filters = String(params.filters ?? '');
  // Honour the caller's Configure list; `['*']` (or an absent list) still means "everything".
  const requestedAttributes = normalizeFacetsParam(params.attributesToRetrieve);
  const attributesToRetrieve =
    requestedAttributes.length === 0 || requestedAttributes.includes('*') ? ['*'] : requestedAttributes;
  // Deduped: InstantSearch merges the Configure list with each widget's own declared facet, so an
  // attribute that is both configured and refinable arrives twice — and every duplicate would cost
  // another full pass over the hit set in computeFacets.
  const requestedFacets = Array.from(new Set(normalizeFacetsParam(params.facets)));
  const wantsAllFacets = requestedFacets.length === 0 || requestedFacets.includes('*');
  const dataset = await getDataset(base, query, filters, attributesToRetrieve, requestedFacets);

  let hits = dataset.hits.filter(
    (hit) => matchesFacetFilters(hit, params.facetFilters) && matchesNumericFilters(hit, params.numericFilters),
  );

  if (sort !== 'default') {
    hits = [...hits].sort((a, b) => {
      const order = sortableName(a).localeCompare(sortableName(b), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sort === 'name_asc' ? order : -order;
    });
  }

  // On a `facets: ['*']` request the probe discovered every facetable attribute on the index (54 on
  // caroma-au-main), but a facet whose attribute was never RETRIEVED has no values on these hits, so
  // computeFacets would scan all of them only to find nothing and skip it. Filtering by the retrieve
  // list first cuts the loop to the handful that can actually populate — identical output, a fraction
  // of the work, and it runs on every refinement.
  const facetNames = wantsAllFacets
    ? dataset.facetNames.filter((name) => isRetrievedAttribute(name, attributesToRetrieve))
    : requestedFacets;
  const maxValuesPerFacet = Number(params.maxValuesPerFacet) > 0 ? Number(params.maxValuesPerFacet) : 100;
  const { facets, facetsStats } = computeFacets(hits, facetNames, maxValuesPerFacet);

  const hitsPerPage = params.hitsPerPage === undefined ? 20 : Number(params.hitsPerPage);
  const page = Number(params.page) > 0 ? Number(params.page) : 0;
  const nbHits = hits.length;
  const nbPages = hitsPerPage > 0 ? Math.ceil(nbHits / hitsPerPage) : 0;
  const pageHits = hitsPerPage > 0 ? hits.slice(page * hitsPerPage, (page + 1) * hitsPerPage) : [];

  return {
    hits: pageHits,
    nbHits,
    page,
    nbPages,
    hitsPerPage,
    facets,
    facets_stats: Object.keys(facetsStats).length > 0 ? facetsStats : undefined,
    exhaustiveFacetsCount: true,
    exhaustiveNbHits: true,
    query,
    params: '',
    index: request.indexName,
    processingTimeMS: 0,
    renderingContent: dataset.renderingContent,
  };
};

export const createCachedProductsSearchClient = (client = searchClient): typeof searchClient => {
  return {
    ...client,
    search: async (requests: any) => {
      const slots: Promise<any>[] = new Array(requests.length);
      const passthrough: { request: AnyRecord; index: number }[] = [];

      (requests as AnyRecord[]).forEach((request, index) => {
        const match = resolveProductIndex(request.indexName);
        const params = request.params || {};
        // Datasets are scoped by Configure `filters` / `query`; a request with neither
        // (e.g. the pre-Configure mount query) would preload the whole index — pass it through.
        const cacheable = match && (String(params.filters ?? '').trim() || String(params.query ?? '').trim());
        if (match && cacheable) {
          // On any preload/compute failure, degrade gracefully to a direct Algolia call
          slots[index] = answerFromDataset(request, match.base, match.sort).catch(() =>
            (client.search([request] as any) as Promise<any>).then((response: any) => response.results[0]),
          );
        } else if (match) {
          // Product index but no filters/query yet (pre-Configure mount) — don't leak the
          // unfiltered index; return an artificial empty result instead.
          slots[index] = Promise.resolve(artificialEmptyResult(request));
        } else {
          passthrough.push({ request, index });
        }
      });

      if (passthrough.length > 0) {
        const remote = client.search(passthrough.map((p) => p.request) as any) as Promise<any>;
        passthrough.forEach((p, j) => {
          slots[p.index] = remote.then((response: any) => response.results[j]);
        });
      }

      const results = await Promise.all(slots);
      return { results } as any;
    },
  } as typeof searchClient;
};

export const cachedProductsSearchClient = createCachedProductsSearchClient();
